import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { KeyPersonIcon } from 'framework/ui/mui-builder';
import { memo } from 'react';
import { ProjectReviewSection } from '../../types';
import { PROJECT_REVIEW_SECTION_LABELS } from '../../constants';
import { getEntityCounts } from '../utils';
import CountBadges from './CountBadges';
import OwnerNameLabel from './OwnerNameLabel';
import ReviewSectionAccordion from './ReviewSectionAccordion';

interface ControlPlaneAccordionProps {
  comments: Record<string, string>;
  defaultExpanded: boolean;
  isCommentingDisabled: boolean;
  openComments: Set<string>;
  owner?: string;
  section: ProjectReviewSection;
  onCommentChange?: (entityId: string, value: string) => void;
  onCommentToggle?: (entityId: string) => void;
}

const ControlPlaneAccordion = ({
  comments,
  defaultExpanded,
  isCommentingDisabled,
  openComments,
  owner,
  section,
  onCommentChange,
  onCommentToggle,
}: ControlPlaneAccordionProps) => {
  const groupsWithEntities = section.groups.filter(group => group.entities.length > 0);
  const { changesCount, staleItemsCount } = getEntityCounts(groupsWithEntities.flatMap(group => group.entities));

  return (
    <Accordion defaultExpanded={defaultExpanded} disableGutters square sx={{ mb: 1, '&:before': { display: 'none' } }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          minHeight: 52,
          bgcolor: 'background.default',
          flexDirection: 'row-reverse',
          gap: 1,
          '& .MuiAccordionSummary-content': {
            my: 0,
            minWidth: 0,
          },
        }}
      >
        <Box
          display="grid"
          gridTemplateColumns="minmax(0, 1fr) auto minmax(0, 1fr)"
          alignItems="center"
          width="100%"
          gap={2}
        >
          <Typography variant="h6" color="primary" noWrap>
            {PROJECT_REVIEW_SECTION_LABELS[section.section]}
          </Typography>
          {owner ? (
            <Box display="flex" alignItems="center" justifyContent="center" gap={0.75} minWidth={0}>
              <KeyPersonIcon fontSize="small" />
              <OwnerNameLabel owner={owner} />
            </Box>
          ) : (
            <Box />
          )}
          <Box display="flex" justifyContent="flex-end" minWidth={0}>
            <CountBadges changesCount={changesCount} staleItemsCount={staleItemsCount} />
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0, pb: 0 }}>
        {groupsWithEntities.length ? (
          groupsWithEntities.map(group => (
            <ReviewSectionAccordion
              key={group.entityType}
              comments={comments}
              group={group}
              isCommentingDisabled={isCommentingDisabled}
              openComments={openComments}
              onCommentChange={onCommentChange}
              onCommentToggle={onCommentToggle}
            />
          ))
        ) : (
          <Box sx={{ border: '1px solid var(--mui-palette-border-default)', px: 1.25, py: 1, mb: 1 }}>
            <Typography variant="body1">No changes or stale items during the selected review period.</Typography>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default memo(ControlPlaneAccordion);
